import React, { useState } from 'react';
import { format } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import { Eye, Filter, X } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  Button,
  Badge,
  Modal,
} from '../ui';
import Pagination from '../ui/Pagination';
import { surveyService } from '../../services/survey.service';
import userAccountService from '../../services/user-account.service';
import { ResponseDetail } from './ResponseDetail';
import { ResponsesChart } from './ResponsesChart';
import type { SurveyResponse } from '../../types/survey';

interface ResponseListProps {
  questionnaireId: number;
}

interface Filters {
  date_from: string;
  date_to: string;
  option_id: string;
  user_account_id: string;
}

const getDefaultFilters = (): Filters => {
  const now = new Date();
  const firstDay = format(new Date(now.getFullYear(), now.getMonth(), 1), 'yyyy-MM-dd');
  const lastDay = format(new Date(now.getFullYear(), now.getMonth() + 1, 0), 'yyyy-MM-dd');
  return { date_from: firstDay, date_to: lastDay, option_id: '', user_account_id: '' };
};

const PER_PAGE = 20;

export const ResponseList: React.FC<ResponseListProps> = ({ questionnaireId }) => {
  const [selectedResponse, setSelectedResponse] = useState<SurveyResponse | null>(null);
  const [filters, setFilters] = useState<Filters>(getDefaultFilters);
  const [appliedFilters, setAppliedFilters] = useState<Filters>(getDefaultFilters);
  const [currentPage, setCurrentPage] = useState(1);

  const { data: questions = [] } = useQuery({
    queryKey: ['survey-questions', questionnaireId],
    queryFn: () => surveyService.listQuestions(questionnaireId),
  });

  const { data: userAccounts = [] } = useQuery({
    queryKey: ['user-accounts'],
    queryFn: () => userAccountService.getAccounts(),
  });

  const allOptions = questions.flatMap((q) =>
    (q.options || []).map((opt) => ({ ...opt, question_text: q.question_text }))
  );

  const activeFilters = {
    date_from: appliedFilters.date_from || undefined,
    date_to: appliedFilters.date_to || undefined,
    option_id: appliedFilters.option_id ? Number(appliedFilters.option_id) : undefined,
    user_account_id: appliedFilters.user_account_id ? Number(appliedFilters.user_account_id) : undefined,
  };

  const { data: paginatedResult, isLoading } = useQuery({
    queryKey: ['survey-responses', questionnaireId, activeFilters, currentPage],
    queryFn: () => surveyService.listResponses(questionnaireId, {
      ...activeFilters,
      page: currentPage,
      limit: PER_PAGE,
    }),
  });

  const responses = paginatedResult?.data ?? [];
  const pagination = paginatedResult?.pagination;

  const hasActiveFilters =
    !!appliedFilters.date_from ||
    !!appliedFilters.date_to ||
    !!appliedFilters.option_id ||
    !!appliedFilters.user_account_id;

  const handleApply = () => {
    setCurrentPage(1);
    setAppliedFilters(filters);
  };

  const handleClear = () => {
    const defaults = getDefaultFilters();
    setFilters(defaults);
    setAppliedFilters(defaults);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  if (isLoading) {
    return <div className="text-sm text-gray-500 py-4">Carregando respostas...</div>;
  }

  return (
    <div>
      {/* Filters panel */}
      <div className="mb-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="h-4 w-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filtros</span>
        </div>
        <div className="space-y-3">
          {/* Row 1: Data início | Data fim */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Data início</label>
              <input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
                className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Data fim</label>
              <input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
                className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          {/* Row 2: Atendente */}
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Atendente</label>
            <select
              value={filters.user_account_id}
              onChange={(e) => setFilters((f) => ({ ...f, user_account_id: e.target.value }))}
              className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos os atendentes</option>
              <option value="0">Nenhum atendente / Assistente virtual</option>
              {userAccounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name}
                </option>
              ))}
            </select>
          </div>
          {/* Row 3: Opção selecionada */}
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Opção selecionada</label>
            <select
              value={filters.option_id}
              onChange={(e) => setFilters((f) => ({ ...f, option_id: e.target.value }))}
              className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todas as opções</option>
              {allOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.option_text}
                  {opt.option_value ? ` (${opt.option_value})` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <Button size="sm" onClick={handleApply}>
            Aplicar filtros
          </Button>
          {hasActiveFilters && (
            <Button size="sm" variant="secondary" onClick={handleClear}>
              <X className="h-3 w-3 mr-1" />
              Limpar
            </Button>
          )}
        </div>
      </div>

      {pagination && (
        <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
          Total de respostas: <span className="font-medium">{pagination.total}</span>
          {hasActiveFilters && (
            <span className="ml-2 text-blue-600 dark:text-blue-400">(filtrado)</span>
          )}
        </div>
      )}

      <ResponsesChart questionnaireId={questionnaireId} filters={activeFilters} />

      {responses.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
          Nenhuma resposta encontrada{hasActiveFilters ? ' para os filtros aplicados' : ' para este questionário'}.
        </div>
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Ticket</TableHead>
                  <TableHead>Contato</TableHead>
                  <TableHead>Atendente</TableHead>
                  <TableHead>Respostas</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {responses.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{r.id}</span>
                    </TableCell>
                    <TableCell>
                      {r.ticket_id ? (
                        <Badge variant="default">{r.ticket_id}</Badge>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-700 dark:text-gray-300">
                        {r.contact_name ?? <span className="text-gray-400">-</span>}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-700 dark:text-gray-300">
                        {r.user_account_name ?? <span className="text-gray-400">-</span>}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="success">{r.answer_count}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <div className="text-gray-900 dark:text-gray-100">
                          {format(new Date(r.created_at), 'dd/MM/yyyy')}
                        </div>
                        <div className="text-gray-500">
                          {format(new Date(r.created_at), 'HH:mm')}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => setSelectedResponse(r)}
                          title="Ver detalhes"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {pagination && pagination.last_page > 1 && (
            <div className="mt-4">
              <Pagination
                currentPage={pagination.current_page}
                totalPages={pagination.last_page}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </>
      )}

      <Modal
        isOpen={!!selectedResponse}
        onClose={() => setSelectedResponse(null)}
        title={`Detalhes da Resposta #${selectedResponse?.id}`}
      >
        {selectedResponse && (
          <ResponseDetail
            responseId={selectedResponse.id}
            onClose={() => setSelectedResponse(null)}
          />
        )}
      </Modal>
    </div>
  );
};
